import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import type { SxProps, Theme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type React from "react";
import { useTranslation } from "react-i18next";
import type { Order } from "../util/types";
import TableSortLabel from "./TableSortLabel";

export type Cell<COLUMN_ID_TYPE extends React.Key> = {
  id: COLUMN_ID_TYPE;
  numeric: boolean;
  label: string;
  sortable?: boolean;
  disablePadding?: boolean;
};
type EnhancedTableHeadArgs<COLUMN_ID_TYPE extends React.Key> = {
  headCells: Array<Cell<COLUMN_ID_TYPE>>;
  headSx?: SxProps<Theme>;
  order: Order;
  orderBy: COLUMN_ID_TYPE;
  onRequestSort: (event: React.MouseEvent<HTMLSpanElement>, column: COLUMN_ID_TYPE) => void;
  selectAll?: boolean;
  onSelectAllClick?: (event: {
    target: {
      checked: boolean;
    };
  }) => void;
  numSelected?: number;
  rowCount: number;
  emptyCol?: boolean;
};
export default function EnhancedTableHead<COLUMN_ID_TYPE extends React.Key>(
  props: EnhancedTableHeadArgs<COLUMN_ID_TYPE>,
): React.ReactNode {
  const {
    headCells,
    headSx,
    order,
    orderBy,
    onRequestSort,
    selectAll,
    onSelectAllClick,
    numSelected,
    rowCount,
    emptyCol,
  } = props;
  const createSortHandler = (property: COLUMN_ID_TYPE) => (event: React.MouseEvent<HTMLSpanElement>) => {
    onRequestSort(event, property);
  };
  const { t } = useTranslation("common");
  return (
    <TableHead>
      <TableRow>
        {selectAll && typeof numSelected === "number" && (
          <TableCell padding="checkbox" sx={headSx}>
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              slotProps={{
                input: {
                  "aria-label": t("enhancedTableHead.selectAllLabel"),
                },
              }}
            />
          </TableCell>
        )}
        {emptyCol && <TableCell padding="checkbox" sx={headSx} />}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            {...(orderBy === headCell.id && {
              sortDirection: order,
            })}
            sx={headSx}
          >
            {(typeof headCell.sortable === "undefined" || headCell.sortable === true) && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={order}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box
                    component="span"
                    sx={{
                      border: 0,
                      clip: "rect(0 0 0 0)",
                      height: 1,
                      margin: -1,
                      overflow: "hidden",
                      p: 0,
                      position: "absolute",
                      top: 20,
                      width: 1,
                    }}
                  >
                    {order === "desc"
                      ? t("enhancedTableHead.sortedDescending")
                      : t("enhancedTableHead.sortedAscending")}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
            {headCell.sortable === false && headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
